import React, { useState, useRef, useEffect } from 'react'
import { Button, Col, Input, message, Modal, Row } from 'antd'
import { Map, Marker, TileLayer } from 'react-leaflet'
import { DivIcon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { getLeafletTileConfig } from '../../util/mapTiles'

// Simple pin (avoids the broken default-marker-image issue with webpack + leaflet).
const pinIcon = new DivIcon({
  className: 'location-picker-pin',
  html: '<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:#f5222d;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);transform:rotate(-45deg)"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 18]
})

const DEFAULT_CENTER = [20.5937, 78.9629] // India centroid as a neutral fallback

const isValidCoord = (v) => v !== undefined && v !== null && v !== '' && !Number.isNaN(Number(v))

function LocationPickerModal ({ visible, initialLat, initialLng, initialAddress, onOk, onCancel }) {
  const mapRef = useRef(null)
  const [position, setPosition] = useState(null)
  const [address, setAddress] = useState('')
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (visible) {
      const hasInitial = isValidCoord(initialLat) && isValidCoord(initialLng)
      setPosition(hasInitial ? [Number(initialLat), Number(initialLng)] : null)
      setAddress(initialAddress || '')
      // Leaflet needs a size recalculation once the (previously hidden) modal is visible.
      setTimeout(() => {
        if (mapRef.current && mapRef.current.leafletElement) {
          mapRef.current.leafletElement.invalidateSize()
        }
      }, 200)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const recenter = (lat, lng, zoom = 15) => {
    setPosition([lat, lng])
    if (mapRef.current && mapRef.current.leafletElement) {
      mapRef.current.leafletElement.setView([lat, lng], zoom)
    }
  }

  const handleMapClick = (e) => {
    setPosition([e.latlng.lat, e.latlng.lng])
  }

  const handleSearch = async () => {
    if (!address || !address.trim()) {
      message.warning('Type an address to search')
      return
    }
    setSearching(true)
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address.trim())}`)
      const data = await res.json()
      if (data && data.length) {
        recenter(Number(data[0].lat), Number(data[0].lon))
      } else {
        message.warning('Address not found')
      }
    } catch (e) {
      message.error('Address search failed')
    } finally {
      setSearching(false)
    }
  }

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      message.warning('Geolocation is not supported by your browser')
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => recenter(pos.coords.latitude, pos.coords.longitude),
      () => message.error('Unable to get your current location')
    )
  }

  const handleOk = () => {
    if (!position) {
      message.warning('Please select a location on the map')
      return
    }
    onOk({
      latitude: Number(Number(position[0]).toFixed(6)),
      longitude: Number(Number(position[1]).toFixed(6)),
      address: address || undefined
    })
  }

  const tile = getLeafletTileConfig()
  const center = position || DEFAULT_CENTER

  return (
    <Modal
      title='Select Location'
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText='Use this location'
      width={720}
      destroyOnClose
    >
      <Row gutter={8} style={{ marginBottom: 8 }}>
        <Col style={{ flex: 1 }}>
          <Input
            placeholder='Type an address to center the map'
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
        </Col>
        <Col><Button loading={searching} onClick={handleSearch}>Search</Button></Col>
        <Col><Button icon='environment' onClick={handleUseMyLocation}>My location</Button></Col>
      </Row>

      <div style={{ height: 380, width: '100%' }}>
        <Map
          ref={mapRef}
          center={center}
          zoom={position ? 15 : 5}
          maxZoom={tile.maxZoom}
          onClick={handleMapClick}
          style={{ height: '100%', width: '100%' }}
          attributionControl={false}
        >
          <TileLayer url={tile.url} subdomains={tile.subdomains} attribution={tile.attribution} />
          {position && (
            <Marker
              position={position}
              icon={pinIcon}
              draggable
              onDragend={(e) => {
                const ll = e.target.getLatLng()
                setPosition([ll.lat, ll.lng])
              }}
            />
          )}
        </Map>
      </div>

      <div style={{ marginTop: 8, color: '#595959' }}>
        {position ? `Selected: ${Number(position[0]).toFixed(6)}, ${Number(position[1]).toFixed(6)}` : 'No location selected yet'}
      </div>
      <div style={{ marginTop: 2, fontSize: 12, color: '#8c8c8c' }}>
        Click on the map or drag the pin to set the location.
      </div>
    </Modal>
  )
}

export default LocationPickerModal
